import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';

import { BroadcastCallService } from 'src/app/shared/services/broadcast-call.service';
import { Call4uAudio } from 'src/app/models/Call4uAudio';
import { Call4uAudioService } from 'src/app/shared/services/call4u-audio.service';
import { LeadList } from 'src/app/models/LeadList';
import { ListsService } from 'src/app/shared/services/lists.service';
import {
  PartialSendConfirmService,
  scheduleErrorMessage,
} from 'src/app/shared/components/partial-send-confirm-modal/partial-send-confirm.service';
import { toastOwnError } from 'src/app/shared/interceptors/http-error.interceptor';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { DashboardService } from 'src/app/shared/services/dashboard.service';
import { WalletService } from 'src/app/shared/services/wallet.service';
import { findProductPrice } from 'src/app/models/Wallet';
import { SearchSelectOption } from 'src/app/shared/components/search-select/search-select.component';
import { DEFAULT_SEND_WINDOWS, SendWindow, isInWindow, spParts } from 'src/app/models/SendWindow';
import { SendWindowsService } from 'src/app/shared/services/send-windows.service';
import { callCostRange, callCreditsRange, ceilToCentMicros } from '../broadcast-call-cost';

/** Limites do `BroadcastCallController::store()` (validação inline, sem FormRequest). */
export const BROADCAST_CALL_NAME_MIN = 3;
export const BROADCAST_CALL_NAME_MAX = 100;
export const BROADCAST_CALL_ATTEMPTS_MIN = 1;
export const BROADCAST_CALL_ATTEMPTS_MAX = 5;
export const BROADCAST_CALL_INTERVAL_MIN = 1;
export const BROADCAST_CALL_INTERVAL_MAX = 120;

/**
 * Como a conta paga ligação e o que dá para estimar. `null` = não estimar (cartão escondido).
 * - `wallet`: preço por bloco em micros e saldo disponível em micros.
 * - `credits`: créditos de ligação disponíveis (o mesmo líquido do backend: créditos − reservado).
 */
export type CallPricing =
  | { billing: 'wallet'; unitPriceMicros: number; availableMicros: number; overdraft: boolean }
  | { billing: 'credits'; availableCredits: number };

/** Linha do cartão de custo, já calculada para o template. */
export interface CallCostView {
  billing: 'wallet' | 'credits';
  blocks: number;
  perCall: number;
  max: number;
  /** `max` arredondado PARA CIMA até o centavo, para exibir em 2 casas sem subestimar. */
  maxDisplay: number;
  maxDials: number;
  available: number;
  /** O teto passa do disponível: o backend vai pedir confirmação (T.10) ao criar. */
  short: boolean;
  unitPriceMicros?: number;
}

@Component({
  selector: 'app-broadcast-call',
  templateUrl: './broadcast-call.component.html',
  styleUrls: ['./broadcast-call.component.scss'],
})
export class BroadcastCallComponent implements OnInit, OnDestroy {

  readonly steps = ['Nome e lista', 'Áudio', 'Tentativas', 'Agendamento', 'Confirmação'];
  readonly nameMax = BROADCAST_CALL_NAME_MAX;
  readonly attemptsMin = BROADCAST_CALL_ATTEMPTS_MIN;
  readonly attemptsMax = BROADCAST_CALL_ATTEMPTS_MAX;
  readonly intervalMin = BROADCAST_CALL_INTERVAL_MIN;
  readonly intervalMax = BROADCAST_CALL_INTERVAL_MAX;

  currentStep = 1;
  totalSteps = 5;
  /** Maior passo já aberto: o stepper só deixa pular para passo já visitado (e alcançável). */
  maxVisitedStep = 1;
  isSaving = false;
  subscriptions: Subscription[] = [];
  /** O POST de criar, com a confirmação de saldo e o reenvio: um por vez, cancelado ao sair. */
  private saveSubscription: Subscription | null = null;

  lists: LeadList[] = [];
  audios: Call4uAudio[] = [];
  listOptions: SearchSelectOption[] = [];
  audioOptions: SearchSelectOption[] = [];

  /** Cada select espera a SUA chamada: o nome do disparo já pode ser digitado. */
  loadingLists = true;
  loadingAudios = true;

  /** Janela de ligação (Brasília). `null` enquanto não chega ou se falhar: aí quem decide é o backend. */
  windowCall: SendWindow | null = null;

  /** Preço/saldo para o cartão de custo. `null` = falhou ou não dá para estimar: cartão escondido. */
  pricing: CallPricing | null = null;

  // Form model
  name = '';
  maxAttempts = 2;
  retryIntervalMinutes = 60;
  sendNow = true;
  /** Valor do `app-window-datetime-picker` (CVA em `Date | null`, horário de parede de Brasília). */
  scheduledDate: Date | null = null;
  scheduledEndDate: Date | null = null;
  postbackUrl = '';

  // Cached selections
  private _audioId = '';
  private _leadListId = '';
  private _selectedAudio: Call4uAudio | null = null;
  private _selectedList: LeadList | null = null;

  get audioId(): string { return this._audioId; }
  set audioId(val: string) {
    this._audioId = val || '';
    this._selectedAudio = this.audios.find(a => a.id === this._audioId) || null;
  }

  get leadListId(): string { return this._leadListId; }
  set leadListId(val: string) {
    this._leadListId = val || '';
    this._selectedList = this.lists.find(l => l.id === this._leadListId) || null;
  }

  /** Janela usada pelos componentes de agendamento: nunca `null` (sem janela conhecida ainda,
   * entra o padrão só para os limites do campo de hora — quem trava de verdade é `stepErrors`/`submit`,
   * que seguem olhando `windowCall` puro). */
  get effectiveWindowCall(): SendWindow {
    return this.windowCall || DEFAULT_SEND_WINDOWS.call;
  }

  constructor(
    private broadcastCallService: BroadcastCallService,
    private call4uAudioService: Call4uAudioService,
    private listsService: ListsService,
    private router: Router,
    private toastService: ToastService,
    private partialSendConfirm: PartialSendConfirmService,
    private sendWindowService: SendWindowsService,
    private walletService: WalletService,
    private dashboardService: DashboardService,
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.listsService.listAll().subscribe({
        next: lists => {
          this.loadingLists = false;
          this.lists = lists || [];
          this.listOptions = this.lists.map(list => ({
            value: list.id,
            label: list.name,
            hint: list.leads_count != null ? `${list.leads_count} ${list.leads_count === 1 ? 'contato' : 'contatos'}` : undefined,
          }));
          this.leadListId = this._leadListId;
        },
        error: () => { this.loadingLists = false; },
      })
    );
    this.subscriptions.push(
      this.call4uAudioService.list(1, 100).subscribe({
        next: result => {
          this.loadingAudios = false;
          this.audios = result?.data || [];
          this.audioOptions = this.audios.map(audio => ({
            value: audio.id,
            label: audio.title,
            hint: this.audioHint(audio),
          }));
          this.audioId = this._audioId;
        },
        error: () => { this.loadingAudios = false; },
      })
    );
    this.subscriptions.push(
      this.sendWindowService.get().subscribe({
        next: windows => {
          this.windowCall = windows?.call || null;
          // A janela pode já ter fechado antes do usuário chegar no passo 4: sincroniza aqui
          // para o `app-send-when` nascer com `sendNow` já coerente (`enterStep` repete essa
          // conferência de novo, porque o tempo passa enquanto o resto do formulário é preenchido).
          if (this.windowCall && this.sendNow && !isInWindow(this.windowCall, this.now())) {
            this.sendNow = false;
          }
        },
        error: () => { this.windowCall = null; },
      })
    );
    this.subscriptions.push(
      this.loadPricing().subscribe({
        next: pricing => { this.pricing = pricing; },
        // Qualquer falha esconde o cartão: estimativa errada é pior que nenhuma.
        error: () => { this.pricing = null; },
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.saveSubscription?.unsubscribe();
  }

  /** Relógio da tela; separado para os testes fixarem o "agora". */
  now(): Date {
    return new Date();
  }

  get selectedAudio(): Call4uAudio | null {
    return this._selectedAudio;
  }

  get selectedList(): LeadList | null {
    return this._selectedList;
  }

  // --- Validação por passo ---------------------------------------------------------------

  /** Mensagens que bloqueiam o passo `step` (vazio = passo válido). */
  stepErrors(step: number): string[] {
    const errors: string[] = [];
    switch (step) {
      case 1: {
        const name = (this.name || '').trim();
        if (name.length < BROADCAST_CALL_NAME_MIN) {
          errors.push(`O nome precisa ter pelo menos ${BROADCAST_CALL_NAME_MIN} caracteres.`);
        } else if (name.length > BROADCAST_CALL_NAME_MAX) {
          errors.push(`O nome pode ter no máximo ${BROADCAST_CALL_NAME_MAX} caracteres.`);
        }
        if (!this.leadListId) { errors.push('Selecione uma lista de contatos.'); }
        if (!this.isPostbackUrlValid()) { errors.push('URL inválida. Use um endereço HTTP ou HTTPS completo.'); }
        break;
      }
      case 2:
        if (!this.audioId) { errors.push('Selecione um áudio.'); }
        break;
      case 3:
        if (!this.isAttemptsValid()) {
          errors.push(`Tentativas de ${BROADCAST_CALL_ATTEMPTS_MIN} a ${BROADCAST_CALL_ATTEMPTS_MAX}.`);
        }
        if (this.maxAttempts > 1 && !this.isIntervalValid()) {
          errors.push(`Intervalo entre tentativas: de ${BROADCAST_CALL_INTERVAL_MIN} a ${BROADCAST_CALL_INTERVAL_MAX} minutos.`);
        }
        break;
      case 4:
        if (this.sendNow) {
          if (this.windowCall && !isInWindow(this.windowCall, this.now())) {
            errors.push('Fora da janela de ligação: agende uma data e hora.');
          }
        } else if (!this.scheduledDate || !this.scheduledEndDate) {
          errors.push('Preencha data e hora de início e final.');
        } else {
          if (this.scheduledDate.getTime() <= this.now().getTime()) {
            errors.push('A data de início precisa ser no futuro.');
          }
          if (this.scheduledEndDate.getTime() <= this.scheduledDate.getTime()) {
            errors.push('A data final precisa ser depois da inicial.');
          }
        }
        break;
    }
    return errors;
  }

  isStepValid(step: number): boolean {
    return this.stepErrors(step).length === 0;
  }

  canProceed(): boolean {
    return this.isStepValid(this.currentStep);
  }

  /** Todos os passos antes de `step` estão válidos. */
  canReach(step: number): boolean {
    for (let i = 1; i < step; i++) {
      if (!this.isStepValid(i)) { return false; }
    }
    return true;
  }

  /** Botão do stepper: passo já visitado e alcançável (e não o atual). */
  canGoTo(step: number): boolean {
    return step !== this.currentStep && step <= this.maxVisitedStep && this.canReach(step);
  }

  goTo(step: number): void {
    if (step < 1 || step > this.totalSteps || step === this.currentStep) { return; }
    // Voltar é sempre permitido; avançar só por passo já visitado e com tudo antes válido.
    if (step > this.currentStep && !(step <= this.maxVisitedStep && this.canReach(step))) { return; }
    this.enterStep(step);
  }

  next(): void {
    if (this.currentStep < this.totalSteps && this.canProceed()) {
      this.enterStep(this.currentStep + 1);
    }
  }

  prev(): void {
    if (this.currentStep > 1) {
      this.enterStep(this.currentStep - 1);
    }
  }

  private enterStep(step: number): void {
    this.currentStep = step;
    this.maxVisitedStep = Math.max(this.maxVisitedStep, step);
    // Mesma sincronização do `ngOnInit`: o tempo passa enquanto os passos anteriores são
    // preenchidos, então a janela pode ter fechado só agora. Sincronizar aqui (antes do
    // `app-send-when` nascer) evita que ele precise desligar "Enviar agora" no próprio
    // `ngOnInit` dele — quem decide a disponibilidade é sempre o `app-send-when`, isto só
    // garante que ele já nasça com o valor coerente.
    if (step === 4 && this.windowCall && this.sendNow && !isInWindow(this.windowCall, this.now())) {
      this.sendNow = false;
    }
  }

  isPostbackUrlValid(): boolean {
    const trimmed = (this.postbackUrl || '').trim();
    if (trimmed === '') { return true; }
    try {
      const parsed = new URL(trimmed);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  isAttemptsValid(): boolean {
    const n = Number(this.maxAttempts);
    return Number.isInteger(n) && n >= BROADCAST_CALL_ATTEMPTS_MIN && n <= BROADCAST_CALL_ATTEMPTS_MAX;
  }

  isIntervalValid(): boolean {
    const n = Number(this.retryIntervalMinutes);
    return Number.isInteger(n) && n >= BROADCAST_CALL_INTERVAL_MIN && n <= BROADCAST_CALL_INTERVAL_MAX;
  }

  changeAttempts(delta: number): void {
    const n = (Number(this.maxAttempts) || BROADCAST_CALL_ATTEMPTS_MIN) + delta;
    this.maxAttempts = Math.min(BROADCAST_CALL_ATTEMPTS_MAX, Math.max(BROADCAST_CALL_ATTEMPTS_MIN, n));
  }

  // --- Janela de ligação ------------------------------------------------------------------

  /** Liga ao `(sendNowChange)` do `app-send-when` (e aos testes, que chamam direto). */
  setSendNow(value: boolean): void {
    this.sendNow = value;
  }

  // --- Custo estimado ---------------------------------------------------------------------

  /**
   * Preço e saldo pela mesma régua do backend: o `GET /wallet/billing` diz se a ligação desta
   * conta sai da carteira (R$) ou do pacote (créditos). Carteira: `GET /wallet/prices` (produto
   * `call`, unidade `block_30s`) + `GET /wallet/balance`. Pacote: `GET /user-credits-info`.
   * Qualquer coisa fora do esperado vira `null` (cartão escondido), nunca zero.
   */
  private loadPricing(): Observable<CallPricing | null> {
    return this.walletService.getBilling().pipe(
      switchMap(billing => {
        if (billing?.call === 'wallet') {
          return forkJoin({
            prices: this.walletService.getPrices(),
            balance: this.walletService.getBalance(),
          }).pipe(map(({ prices, balance }): CallPricing | null => {
            const product = findProductPrice(prices, 'call');
            const unit = product?.unit_price_micros;
            if (!product || product.blocked || product.unit !== 'block_30s'
              || typeof unit !== 'number' || !(unit > 0)
              || !balance || typeof balance.available_micros !== 'number') {
              return null;
            }
            return {
              billing: 'wallet',
              unitPriceMicros: unit,
              availableMicros: balance.available_micros,
              overdraft: !!balance.overdraft,
            };
          }));
        }
        if (billing?.call === 'credits') {
          return this.dashboardService.userCreditsInfo().pipe(map((info): CallPricing | null => {
            if (!info || typeof info.call_credits !== 'number') { return null; }
            const available = info.call_credits - (Number(info.call_total_reserved) || 0);
            return { billing: 'credits', availableCredits: available };
          }));
        }
        return of(null);
      }),
    );
  }

  /** O cartão de custo, ou `null` para escondê-lo (preço indisponível, lista/áudio sem número). */
  get cost(): CallCostView | null {
    const pricing = this.pricing;
    const leads = this.selectedList?.leads_count;
    const seconds = this.selectedAudio?.duration_seconds;
    if (!pricing || typeof leads !== 'number' || typeof seconds !== 'number') {
      return null;
    }

    if (pricing.billing === 'wallet') {
      const range = callCostRange({
        leads, attempts: this.maxAttempts, audioSeconds: seconds, unitPriceMicros: pricing.unitPriceMicros,
      });
      if (!range) { return null; }
      return {
        billing: 'wallet',
        blocks: range.blocks,
        perCall: range.perCallMicros,
        max: range.maxMicros,
        maxDisplay: ceilToCentMicros(range.maxMicros),
        maxDials: range.maxDials,
        available: pricing.availableMicros,
        short: !pricing.overdraft && range.maxMicros > pricing.availableMicros,
        unitPriceMicros: pricing.unitPriceMicros,
      };
    }

    const credits = callCreditsRange({ leads, attempts: this.maxAttempts, audioSeconds: seconds });
    if (!credits) { return null; }
    return {
      billing: 'credits',
      blocks: credits.blocks,
      perCall: credits.perCallMicros,
      max: credits.maxMicros,
      maxDisplay: credits.maxMicros,
      maxDials: credits.maxDials,
      available: pricing.availableCredits,
      short: credits.maxMicros > pricing.availableCredits,
    };
  }

  // --- Exibição ---------------------------------------------------------------------------

  formatDuration(seconds: number): string {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    const parts: string[] = [];
    if (min > 0) { parts.push(`${min}min`); }
    parts.push(`${sec}s`);
    return parts.join(' ');
  }

  /** Inteiro com separador pt-BR (Intl direto: o locale pt-BR do Angular não é registrado). */
  formatInt(value: number): string {
    return Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
  }

  /** Date (horário de parede de Brasília) -> 'DD/MM/YYYY HH:mm'. */
  formatLocal(value: Date | null): string {
    if (!value) { return ''; }
    const { year, month, day, hour, minute } = spParts(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(day)}/${pad(month)}/${year} ${pad(hour)}:${pad(minute)}`;
  }

  private audioHint(audio: Call4uAudio): string | undefined {
    const parts: string[] = [];
    if (audio.duration_seconds) { parts.push(this.formatDuration(audio.duration_seconds)); }
    if (audio.status && audio.status !== 'approved') { parts.push('não aprovado'); }
    return parts.length ? parts.join(' · ') : undefined;
  }

  // --- Envio ------------------------------------------------------------------------------

  submit(): void {
    if (this.isSaving) { return; }

    // A janela pode ter fechado enquanto a tela estava aberta.
    if (this.sendNow && this.windowCall && !isInWindow(this.windowCall, this.now())) {
      this.sendNow = false;
      this.toastService.error('Fora da janela de ligação agora. Agende uma data e hora.');
      this.enterStep(4);
      return;
    }

    this.isSaving = true;

    const trimmedPostback = (this.postbackUrl || '').trim();
    const data: any = {
      name: (this.name || '').trim(),
      lead_list_id: this.leadListId,
      call4u_audio_id: this.audioId,
      max_attempts: Number(this.maxAttempts),
      // Com 1 tentativa o intervalo não é usado (nem validado no passo 3): valor fora de 1-120
      // daria 422 à toa, então vai nulo e o backend aplica o padrão dele.
      retry_interval_minutes: Number(this.maxAttempts) === 1 && !this.isIntervalValid()
        ? null
        : Number(this.retryIntervalMinutes),
      scheduled_date: this.sendNow ? null : (this.scheduledDate?.toISOString() || null),
      scheduled_end_date: this.sendNow ? null : (this.scheduledEndDate?.toISOString() || null),
      postback_url: trimmedPostback === '' ? null : trimmedPostback,
    };

    // Saldo abaixo do custo: o backend pede confirmação (422) e o modal decide se reenvia com
    // `confirm_partial` (T.10).
    this.saveSubscription?.unsubscribe();
    this.saveSubscription = this.partialSendConfirm.schedule(
      confirmPartial => this.broadcastCallService.create(data, confirmPartial)
    ).pipe(
      finalize(() => this.isSaving = false)
    ).subscribe({
      next: () => {
        this.toastService.success('Broadcast de voz criado com sucesso!');
        this.router.navigate(['/broadcasts-call']);
      },
      error: err => toastOwnError(this.toastService, scheduleErrorMessage(err, 'Erro ao criar broadcast de voz')),
    });
  }
}
